import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <header className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/jobs" className="flex items-center gap-2 text-blue-700 font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to DentalStaff
          </Link>
          <p className="text-sm text-slate-500">Last updated: February 2026</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-slate-700 leading-relaxed [&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:text-slate-900 [&_h1]:mb-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_li]:my-1.5 [&_a]:text-blue-700 [&_a]:underline hover:[&_a]:text-blue-900 [&_strong]:text-slate-900">
        <div className="not-prose mb-8 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg text-sm text-amber-900">
          <strong>Note:</strong> This document was AI-drafted as a starting point and should be reviewed
          by qualified legal counsel before being treated as a final Privacy Policy. Last updated:
          February 2026.
        </div>

        <h1>Privacy Policy</h1>
        <p>
          This Privacy Policy describes how The Oncall Bot LLC ("The Oncall Bot", "PractCom", "DentalStaff", "we", "us", or
          "our") collects, uses, and shares information about you when you use the DentalStaff by PractCom
          job-matching service ("the Service") accessible at <strong>jobs.practcom.com</strong> and
          related domains.
        </p>
        <p>
          By creating an account or using the Service, you consent to the practices described in this Policy.
          If you do not agree, please do not use the Service.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account information:</strong> name, email address, phone number, password (stored hashed),
            location (city, state, ZIP code), search radius preference, and account creation timestamp.
          </li>
          <li>
            <strong>Professional information:</strong> the roles you are seeking, your years of experience,
            employment-type preferences, and the resume document(s) you upload.
          </li>
          <li>
            <strong>Application information:</strong> the jobs you have applied to, any cover notes you
            provide, and timestamps for those applications.
          </li>
          <li>
            <strong>Usage and device data:</strong> pages visited, actions taken within the Service,
            IP address, browser type, device type, operating system, and login timestamps.
          </li>
          <li>
            <strong>Communications:</strong> the content of messages you send through the Service or to
            our support team.
          </li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>To create and manage your job-seeker account.</li>
          <li>To match you with relevant job openings based on your role preferences and location.</li>
          <li>To send job-alert email notifications consistent with your stated preferences.</li>
          <li>To enable dental practices to receive and review applications you have submitted.</li>
          <li>To send service-related communications (account changes, password resets, terms updates).</li>
          <li>To monitor, debug, and improve the Service.</li>
          <li>To detect and prevent fraud, abuse, or security incidents.</li>
          <li>To comply with applicable laws and respond to lawful requests.</li>
        </ul>

        <h2>3. How We Share Your Information</h2>
        <p>
          <strong>Critical: When you submit an application for a specific job posting, your resume, name,
          email address, phone number (if provided), and any cover note you wrote are shared with the
          dental practice that posted that specific position.</strong> That practice is a verified
          subscriber of PractCom's paid subscription service. We do not share your information with
          practices to which you have not applied.
        </p>
        <p>We may also share information in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service providers:</strong> we share information with companies that help us operate
            the Service (e.g., cloud hosting providers, email delivery vendors, geocoding services).
            These providers are bound by confidentiality obligations and may use your information only to
            provide the contracted services.
          </li>
          <li>
            <strong>Legal compliance:</strong> we may disclose information if required by law, subpoena,
            court order, or other valid legal process, or if we believe in good faith that disclosure is
            necessary to protect rights, safety, or property.
          </li>
          <li>
            <strong>Business transfers:</strong> if PractCom is involved in a merger, acquisition, or sale
            of assets, your information may be transferred as part of that transaction. We will notify
            you and post a notice on the Service before your information is transferred and becomes
            subject to a different privacy policy.
          </li>
        </ul>
        <p>
          <strong>We do not sell personal information to any third party.</strong> We do not share your
          information with advertisers or data brokers. We do not use your information for cross-context
          behavioral advertising.
        </p>

        <h2>4. Data Retention</h2>
        <ul>
          <li>
            <strong>Active accounts:</strong> we retain your information for as long as your account
            remains active.
          </li>
          <li>
            <strong>Deleted accounts:</strong> upon account deletion request, we will remove your
            personal information from active systems within 30 days. Limited backups may persist for up
            to 90 additional days for disaster recovery purposes.
          </li>
          <li>
            <strong>Submitted applications:</strong> applications and the resume snapshot attached to
            them are retained for up to two (2) years following submission to permit dental practices to
            maintain hiring records, then purged. You may request earlier deletion at any time.
          </li>
          <li>
            <strong>Aggregated, anonymized data:</strong> we may retain aggregated statistics that cannot
            be used to identify you indefinitely.
          </li>
        </ul>

        <h2>5. Your Rights</h2>
        <p>You have the following rights regarding your personal information:</p>
        <ul>
          <li><strong>Access:</strong> you may request a copy of the personal information we hold about you.</li>
          <li><strong>Correction:</strong> you may correct inaccurate information through your profile
            settings or by contacting us.</li>
          <li><strong>Deletion:</strong> you may delete your account and request removal of your personal
            information.</li>
          <li><strong>Opt-out of marketing emails:</strong> you may disable job-alert notifications in
            your profile settings or via the unsubscribe link in any email we send.</li>
          <li><strong>Data portability:</strong> you may request your information in a portable,
            machine-readable format.</li>
        </ul>

        <h3>5.1 California Residents (CCPA)</h3>
        <p>
          If you are a California resident, you have additional rights under the California Consumer
          Privacy Act (CCPA) and California Privacy Rights Act (CPRA), including:
        </p>
        <ul>
          <li>The right to know what personal information we have collected about you.</li>
          <li>The right to request deletion of your personal information.</li>
          <li>The right to opt out of the sale or sharing of personal information (note: we do not sell
            personal information).</li>
          <li>The right to non-discrimination for exercising your privacy rights.</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{' '}
          <a href="mailto:admin@practcom.com">admin@practcom.com</a>. We will respond within 45 days.
        </p>

        <h3>5.2 European Economic Area, United Kingdom, and Switzerland Residents (GDPR)</h3>
        <p>
          If you are located in the European Economic Area, the United Kingdom, or Switzerland, you have
          additional rights under the General Data Protection Regulation (GDPR), including the rights of
          access, rectification, erasure, restriction of processing, data portability, and to object to
          processing. The legal bases on which we process your information include (a) the performance of
          a contract (to provide the Service you signed up for), (b) your consent (for marketing
          notifications), and (c) our legitimate interest in operating, securing, and improving the
          Service.
        </p>

        <h2>6. Data Security</h2>
        <p>
          We implement reasonable administrative, technical, and physical safeguards designed to protect
          your information, including:
        </p>
        <ul>
          <li>HTTPS/TLS encryption for all data transmitted between your device and our servers.</li>
          <li>Resume files stored in access-controlled cloud storage with signed, time-limited download URLs.</li>
          <li>Passwords stored as bcrypt-hashed values; we never store plain-text passwords.</li>
          <li>Practice-scoped access controls — dental practices cannot view applicant data for jobs they
            did not post.</li>
          <li>Regular security reviews, dependency updates, and monitoring for suspicious activity.</li>
        </ul>
        <p>
          No system is completely secure, however. If we learn of a security breach affecting your
          personal information, we will notify you in accordance with applicable law.
        </p>

        <h2>7. Children's Privacy</h2>
        <p>
          The Service is not intended for individuals under the age of 18. We do not knowingly collect
          personal information from anyone under 18. If you believe a child has provided us with personal
          information, please contact us and we will delete it.
        </p>

        <h2>8. International Data Transfers</h2>
        <p>
          PractCom is based in the United States, and your information may be processed in the United
          States or any other country in which we, or our service providers, maintain facilities. By
          using the Service, you consent to the transfer of your information to the United States and
          other jurisdictions, which may have data-protection laws different from those of your country
          of residence.
        </p>

        <h2>9. Third-Party Links</h2>
        <p>
          The Service may contain links to third-party websites or services. We are not responsible for
          the privacy practices of those third parties. We encourage you to read their privacy policies
          before providing any information to them.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will
          notify you by email and post a notice on the Service before the changes take effect. Your
          continued use of the Service after the effective date of the revised Policy constitutes your
          acceptance of the changes.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          For questions about this Privacy Policy, to exercise any of your rights, or to request data
          deletion, please contact:
        </p>
        <p>
          <strong>The Oncall Bot LLC</strong><br />
          Privacy Inquiries<br />
          Email: <a href="mailto:admin@practcom.com">admin@practcom.com</a>
        </p>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-slate-500">
          <Link to="/jobs" className="text-blue-700 hover:underline font-semibold">
            DentalStaff by PractCom
          </Link>
          {' · '}
          <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
          {' · '}© {new Date().getFullYear()} The Oncall Bot LLC.
        </div>
      </footer>
    </div>
  );
}
